import { Language, LANGUAGES } from '../translations';

/**
 * Strips any leading language prefix from the pathname
 * e.g. "/en/expeditions/trekking" -> "/expeditions/trekking"
 * e.g. "/en" -> "/"
 * e.g. "/expeditions" -> "/expeditions"
 * e.g. "/es/about" -> "/about"
 */
export function getCleanPath(pathname: string): string {
  if (!pathname) return '/';
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length > 0 && LANGUAGES.includes(parts[0] as Language)) {
    const rest = parts.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/**
 * Extracts the active language and the clean path from a pathname
 */
export function extractLangFromPath(pathname: string): { lang: Language; cleanPath: string; hasPrefix: boolean } {
  if (!pathname) return { lang: 'es', cleanPath: '/', hasPrefix: false };
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length > 0 && LANGUAGES.includes(parts[0] as Language)) {
    const lang = parts[0] as Language;
    const rest = parts.slice(1).join('/');
    return { lang, cleanPath: rest ? `/${rest}` : '/', hasPrefix: true };
  }
  return { lang: 'es', cleanPath: pathname.startsWith('/') ? pathname : `/${pathname}`, hasPrefix: false };
}

/**
 * Returns the localized path given a clean or already localized path
 * e.g. getLocalizedPath('/expeditions', 'en') -> "/en/expeditions"
 * e.g. getLocalizedPath('/en/expeditions', 'es') -> "/expeditions"
 * e.g. getLocalizedPath('/', 'en') -> "/en"
 * e.g. getLocalizedPath('/', 'es') -> "/"
 */
export function getLocalizedPath(pathname: string, targetLang: Language): string {
  if (!pathname) return targetLang === 'es' ? '/' : `/${targetLang}`;

  // Preserve query string and hash if present
  const [pathAndQuery, hash] = pathname.split('#');
  const [path, query] = pathAndQuery.split('?');

  const clean = getCleanPath(path);
  let localized = clean;

  if (targetLang !== 'es') {
    localized = clean === '/' ? `/${targetLang}` : `/${targetLang}${clean}`;
  }

  const queryString = query ? `?${query}` : '';
  const hashString = hash ? `#${hash}` : '';
  return `${localized}${queryString}${hashString}`;
}

/**
 * Formats a duration string according to the target language
 * e.g. "1 Día" -> "1 Day", "4 Días" -> "4 Days", "8 Horas" -> "8 Hours"
 */
export function formatDuration(duration: string | undefined | null, lang: Language): string {
  if (!duration) return '';
  if (lang === 'es') return duration;

  if (lang === 'en') {
    return duration
      .replace(/(\d+)\s*d[ií]as?/gi, (_, n) => `${n} ${parseInt(n, 10) === 1 ? 'Day' : 'Days'}`)
      .replace(/(\d+)\s*horas?/gi, (_, n) => `${n} ${parseInt(n, 10) === 1 ? 'Hour' : 'Hours'}`)
      .replace(/medio\s*d[ií]a/gi, 'Half Day')
      .replace(/d[ií]a\s*completo/gi, 'Full Day');
  }

  if (lang === 'pt') {
    return duration
      .replace(/(\d+)\s*d[ií]as?/gi, (_, n) => `${n} ${parseInt(n, 10) === 1 ? 'Dia' : 'Dias'}`)
      .replace(/(\d+)\s*horas?/gi, (_, n) => `${n} ${parseInt(n, 10) === 1 ? 'Hora' : 'Horas'}`)
      .replace(/medio\s*d[ií]a/gi, 'Meio Dia')
      .replace(/d[ií]a\s*completo/gi, 'Dia Inteiro');
  }

  if (lang === 'fr') {
    return duration
      .replace(/(\d+)\s*d[ií]as?/gi, (_, n) => `${n} ${parseInt(n, 10) === 1 ? 'Jour' : 'Jours'}`)
      .replace(/(\d+)\s*horas?/gi, (_, n) => `${n} ${parseInt(n, 10) === 1 ? 'Heure' : 'Heures'}`)
      .replace(/medio\s*d[ií]a/gi, 'Demi-journée')
      .replace(/d[ií]a\s*completo/gi, 'Journée complète');
  }

  return duration;
}
